import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogClose } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { 
  LayoutDashboard, 
  Users, 
  CreditCard, 
  Receipt,
  Upload,
  Send,
  LogOut,
  Eye,
  Pencil,
  Trash2,
  Plus,
  AlertTriangle,
  CheckCircle,
  Download,
  ArrowUpRight,
  ArrowDownRight,
  Circle,
  CheckCircle2,
  XCircle,
  FileSpreadsheet
} from 'lucide-react';
import { Toaster, toast } from 'sonner';
import TransactionOperations from '../components/TransactionOperations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('summary');
  const [summary, setSummary] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [previewUser, setPreviewUser] = useState(null);
  const [previewData, setPreviewData] = useState({ accounts: [], transactions: [] });

  // Form states
  const [editAccount, setEditAccount] = useState(null);
  const [editTransaction, setEditTransaction] = useState(null);
  const [newTransaction, setNewTransaction] = useState({
    user_id: '',
    account_id: '',
    date: '',
    description: '',
    debit_or_credit: 'Debit',
    amount: ''
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadUserId, setUploadUserId] = useState('');
  const [uploadAccountId, setUploadAccountId] = useState('');
  
  // Publish selection states
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [selectedTransactions, setSelectedTransactions] = useState([]);
  const [publishUserFilter, setPublishUserFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [summaryRes, accountsRes, transactionsRes, uploadsRes] = await Promise.all([
        axios.get(`${API}/admin/summary`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/accounts`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/transactions`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/uploads`, { headers, withCredentials: true })
      ]);
      setSummary(summaryRes.data);
      setAccounts(accountsRes.data);
      setTransactions(transactionsRes.data);
      setUploads(uploadsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Account operations
  const handleUpdateAccount = async () => {
    if (!editAccount) return;
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/accounts/${editAccount.id}`, editAccount, { headers, withCredentials: true });
      toast.success('Account updated successfully');
      setEditAccount(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update account');
    }
  };

  // Transaction operations
  const handleCreateTransaction = async () => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/transactions`, {
        ...newTransaction,
        amount: parseFloat(newTransaction.amount)
      }, { headers, withCredentials: true });
      toast.success('Transaction created successfully');
      setNewTransaction({
        user_id: '',
        account_id: '',
        date: '',
        description: '',
        debit_or_credit: 'Debit',
        amount: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to create transaction');
    }
  };

  const handleUpdateTransaction = async () => {
    if (!editTransaction) return;
    try {
      const headers = getAuthHeaders();
      await axios.put(`${API}/admin/transactions/${editTransaction.id}`, editTransaction, { headers, withCredentials: true });
      toast.success('Transaction updated successfully');
      setEditTransaction(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to update transaction');
    }
  };

  const handleDeleteTransaction = async (id) => {
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/admin/transactions/${id}`, { headers, withCredentials: true });
      toast.success('Transaction deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete transaction');
    }
  };

  // Upload operations
  const handleUpload = async () => {
    if (!uploadFile || !uploadUserId || !uploadAccountId) {
      toast.error('Please select a file, user, and account');
      return;
    }
    try {
      const headers = getAuthHeaders();
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('user_id', uploadUserId);
      formData.append('account_id', uploadAccountId);
      
      const response = await axios.post(`${API}/admin/upload`, formData, { 
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
        withCredentials: true 
      });
      toast.success(`Upload complete: ${response.data.transactions_added} added, ${response.data.duplicates_found} duplicates`);
      setUploadFile(null);
      setUploadUserId('');
      setUploadAccountId('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  // Publish operations
  const handlePublish = async (userId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/publish`, { user_id: userId }, { headers, withCredentials: true });
      toast.success('Data published to user dashboard');
      fetchData();
    } catch (error) {
      toast.error('Failed to publish data');
    }
  };

  const handleUnpublish = async (userId) => {
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/unpublish`, { user_id: userId }, { headers, withCredentials: true });
      toast.success('Data unpublished from user dashboard');
      fetchData();
    } catch (error) {
      toast.error('Failed to unpublish data');
    }
  };

  // Individual item publish toggle
  const handleToggleAccountPublish = async (accountId) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.put(`${API}/admin/accounts/${accountId}/publish`, {}, { headers, withCredentials: true });
      toast.success(`Account ${response.data.published_status ? 'published' : 'unpublished'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle account publish status');
    }
  };

  const handleToggleTransactionPublish = async (transactionId) => {
    try {
      const headers = getAuthHeaders();
      const response = await axios.put(`${API}/admin/transactions/${transactionId}/publish`, {}, { headers, withCredentials: true });
      toast.success(`Transaction ${response.data.published_status ? 'published' : 'unpublished'}`);
      fetchData();
    } catch (error) {
      toast.error('Failed to toggle transaction publish status');
    }
  };

  // Bulk selection handlers
  const handleSelectAccount = (accountId) => {
    setSelectedAccounts(prev => 
      prev.includes(accountId) 
        ? prev.filter(id => id !== accountId)
        : [...prev, accountId]
    );
  };

  const handleSelectTransaction = (txId) => {
    setSelectedTransactions(prev => 
      prev.includes(txId) 
        ? prev.filter(id => id !== txId)
        : [...prev, txId]
    );
  };

  const handleSelectAllAccounts = (userId = null) => {
    const targetAccounts = userId 
      ? accounts.filter(a => a.user_id === userId)
      : accounts;
    const allSelected = targetAccounts.every(a => selectedAccounts.includes(a.id));
    
    if (allSelected) {
      setSelectedAccounts(prev => prev.filter(id => !targetAccounts.map(a => a.id).includes(id)));
    } else {
      const newIds = targetAccounts.map(a => a.id);
      setSelectedAccounts(prev => [...new Set([...prev, ...newIds])]);
    }
  };

  const handleSelectAllTransactions = (userId = null) => {
    const targetTx = userId 
      ? transactions.filter(t => t.user_id === userId)
      : transactions;
    const allSelected = targetTx.every(t => selectedTransactions.includes(t.id));
    
    if (allSelected) {
      setSelectedTransactions(prev => prev.filter(id => !targetTx.map(t => t.id).includes(id)));
    } else {
      const newIds = targetTx.map(t => t.id);
      setSelectedTransactions(prev => [...new Set([...prev, ...newIds])]);
    }
  };

  // Bulk publish/unpublish selected items
  const handlePublishSelectedAccounts = async (publish) => {
    if (selectedAccounts.length === 0) {
      toast.error('No accounts selected');
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/accounts/publish-selected`, {
        item_ids: selectedAccounts,
        published_status: publish
      }, { headers, withCredentials: true });
      toast.success(`${selectedAccounts.length} account(s) ${publish ? 'published' : 'unpublished'}`);
      setSelectedAccounts([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to update accounts');
    }
  };

  const handlePublishSelectedTransactions = async (publish) => {
    if (selectedTransactions.length === 0) {
      toast.error('No transactions selected');
      return;
    }
    try {
      const headers = getAuthHeaders();
      await axios.post(`${API}/admin/transactions/publish-selected`, {
        item_ids: selectedTransactions,
        published_status: publish
      }, { headers, withCredentials: true });
      toast.success(`${selectedTransactions.length} transaction(s) ${publish ? 'published' : 'unpublished'}`);
      setSelectedTransactions([]);
      fetchData();
    } catch (error) {
      toast.error('Failed to update transactions');
    }
  };

  // Preview user dashboard
  const handlePreview = async (userId) => {
    try {
      const headers = getAuthHeaders();
      const [accountsRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/admin/preview/${userId}/accounts`, { headers, withCredentials: true }),
        axios.get(`${API}/admin/preview/${userId}/transactions`, { headers, withCredentials: true })
      ]);
      setPreviewData({ accounts: accountsRes.data, transactions: transactionsRes.data });
      setPreviewUser(summary.find(s => s.user.id === userId));
    } catch (error) {
      toast.error('Failed to load preview');
    }
  };

  const getUserAccounts = (userId) => accounts.filter(a => a.user_id === userId);
  const getUserTransactions = (userId) => transactions.filter(t => t.user_id === userId);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      <Toaster position="top-right" richColors />
      
      {/* Sidebar */}
      <aside className="w-64 bg-card border-r border-border min-h-screen p-4 flex flex-col">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-accent/20 flex items-center justify-center border border-accent/30">
            <LayoutDashboard className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-semibold">Admin Panel</h1>
            <p className="text-xs text-muted-foreground">{user?.username}</p>
          </div>
        </div>

        <nav className="flex-1 space-y-1">
          <Button
            variant={activeTab === 'summary' ? 'secondary' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setActiveTab('summary')}
            data-testid="nav-summary"
          >
            <LayoutDashboard className="w-4 h-4 mr-3" />
            Summary
          </Button>
          <Button
            variant={activeTab === 'accounts' ? 'secondary' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setActiveTab('accounts')}
            data-testid="nav-accounts"
          >
            <CreditCard className="w-4 h-4 mr-3" />
            Accounts
          </Button>
          <Button
            variant={activeTab === 'transactions' ? 'secondary' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setActiveTab('transactions')}
            data-testid="nav-transactions"
          >
            <Receipt className="w-4 h-4 mr-3" />
            Transactions
          </Button>
          <Button
            variant={activeTab === 'upload' ? 'secondary' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setActiveTab('upload')}
            data-testid="nav-upload"
          >
            <Upload className="w-4 h-4 mr-3" />
            Upload
          </Button>
          <Button
            variant={activeTab === 'publish' ? 'secondary' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setActiveTab('publish')}
            data-testid="nav-publish"
          >
            <Send className="w-4 h-4 mr-3" />
            Publish
          </Button>
          
          <Separator className="my-2" />
          
          <Button
            variant={activeTab === 'mercury' ? 'secondary' : 'ghost'}
            className="w-full justify-start"
            onClick={() => setActiveTab('mercury')}
            data-testid="nav-mercury"
          >
            <FileSpreadsheet className="w-4 h-4 mr-3" />
            Transaction Ops
          </Button>
        </nav>

        <Button 
          variant="outline" 
          onClick={handleLogout}
          className="mt-auto border-border hover:bg-secondary"
          data-testid="admin-logout"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Summary Tab */}
        {activeTab === 'summary' && (
          <div className="animate-fade-in">
            <h2 className="font-heading text-2xl font-semibold mb-6">Team Summary</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {summary.map((item) => (
                <Card key={item.user.id} className="bg-card border-border" data-testid={`user-card-${item.user.internal_code}`}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge variant="outline" className="bg-accent/20 border-accent text-white">
                          {item.user.internal_code}
                        </Badge>
                      </div>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" onClick={() => handlePreview(item.user.id)} data-testid={`preview-${item.user.internal_code}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-3xl bg-card border-border">
                          <DialogHeader>
                            <DialogTitle className="font-heading">
                              Preview: {previewUser?.user?.internal_code} Dashboard
                            </DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 max-h-96 overflow-auto">
                            <div className="grid grid-cols-2 gap-4">
                              {previewData.accounts.filter(acc => acc.account_type !== 'Treasury').map(acc => (
                                <Card key={acc.id} className="bg-secondary border-border">
                                  <CardContent className="p-4">
                                    <p className="text-xs text-muted-foreground uppercase">{acc.account_type} Account</p>
                                    <p className="font-heading text-xl">{formatCurrency(acc.current_balance)}</p>
                                    <p className="text-xs text-muted-foreground mt-2">{acc.account_number_masked}</p>
                                  </CardContent>
                                </Card>
                              ))}
                            </div>
                            {/* Treasury Account Preview - balance only */}
                            {previewData.accounts.filter(acc => acc.account_type === 'Treasury').map(acc => (
                              <Card key={acc.id} className="bg-secondary/50 border-border border-dashed">
                                <CardContent className="p-4">
                                  <p className="text-xs text-muted-foreground uppercase">{acc.display_name}</p>
                                  <p className="font-heading text-lg text-blue-400">{formatCurrency(acc.current_balance)}</p>
                                  <p className="text-xs text-muted-foreground mt-1">Informational only - balance display</p>
                                </CardContent>
                              </Card>
                            ))}
                            {previewData.accounts.length === 0 && (
                              <p className="text-sm text-muted-foreground text-center py-4">No published accounts</p>
                            )}
                            <Separator />
                            <p className="text-sm font-medium">{previewData.transactions.length} Published Transactions</p>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Spend</p>
                        <p className="font-heading text-xl text-emerald-400">{formatCurrency(item.spend_balance)}</p>
                      </div>
                      {item.spend_published ? (
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      )}
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Hold</p>
                        <p className="font-heading text-xl text-red-400">{formatCurrency(item.hold_balance)}</p>
                      </div>
                      {item.hold_published ? (
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      )}
                    </div>
                    <Separator />
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-muted-foreground uppercase">Treasury (SQI, BVI)</p>
                        <p className="font-heading text-lg text-blue-400">{formatCurrency(item.treasury_balance)}</p>
                      </div>
                      {item.treasury_published ? (
                        <CheckCircle className="w-4 h-4 text-emerald-400" />
                      ) : (
                        <AlertTriangle className="w-4 h-4 text-yellow-400" />
                      )}
                    </div>
                    <Separator />
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="flex-1 bg-success hover:bg-success/90"
                        onClick={() => handlePublish(item.user.id)}
                        data-testid={`publish-${item.user.internal_code}`}
                      >
                        <Send className="w-3 h-3 mr-1" /> Publish
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1"
                        onClick={() => handleUnpublish(item.user.id)}
                        data-testid={`unpublish-${item.user.internal_code}`}
                      >
                        Unpublish
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <div className="animate-fade-in">
            <h2 className="font-heading text-2xl font-semibold mb-6">Account Management</h2>
            
            <Tabs defaultValue={summary[0]?.user?.id} className="space-y-4">
              <TabsList className="bg-secondary">
                {summary.map(item => (
                  <TabsTrigger key={item.user.id} value={item.user.id} className="data-[state=active]:bg-accent">
                    {item.user.internal_code}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {summary.map(item => (
                <TabsContent key={item.user.id} value={item.user.id}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {getUserAccounts(item.user.id).map(account => (
                      <Card key={account.id} className="bg-card border-border" data-testid={`account-${account.id}`}>
                        <CardHeader>
                          <div className="flex items-center justify-between">
                            <CardTitle className="font-heading text-lg">{account.display_name}</CardTitle>
                            <Dialog>
                              <DialogTrigger asChild>
                                <Button variant="ghost" size="sm" onClick={() => setEditAccount({...account})} data-testid={`edit-account-${account.id}`}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="bg-card border-border">
                                <DialogHeader>
                                  <DialogTitle className="font-heading">Edit Account</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-4">
                                  <div>
                                    <Label>Display Name</Label>
                                    <Input 
                                      value={editAccount?.display_name || ''} 
                                      onChange={(e) => setEditAccount({...editAccount, display_name: e.target.value})}
                                      className="bg-background"
                                    />
                                  </div>
                                  <div>
                                    <Label>Balance</Label>
                                    <Input 
                                      type="number"
                                      value={editAccount?.current_balance || ''} 
                                      onChange={(e) => setEditAccount({...editAccount, current_balance: parseFloat(e.target.value)})}
                                      className="bg-background"
                                    />
                                  </div>
                                  <div>
                                    <Label>Masked Account Number</Label>
                                    <Input 
                                      value={editAccount?.account_number_masked || ''} 
                                      onChange={(e) => setEditAccount({...editAccount, account_number_masked: e.target.value})}
                                      className="bg-background"
                                    />
                                  </div>
                                  <div>
                                    <Label>Sort Code Display</Label>
                                    <Input 
                                      value={editAccount?.sort_code_display || ''} 
                                      onChange={(e) => setEditAccount({...editAccount, sort_code_display: e.target.value})}
                                      className="bg-background"
                                    />
                                  </div>
                                  <div>
                                    <Label>Full Account Number (Admin Only)</Label>
                                    <Input 
                                      value={editAccount?.full_account_number || ''} 
                                      onChange={(e) => setEditAccount({...editAccount, full_account_number: e.target.value})}
                                      className="bg-background"
                                    />
                                  </div>
                                </div>
                                <DialogFooter>
                                  <DialogClose asChild>
                                    <Button variant="outline">Cancel</Button>
                                  </DialogClose>
                                  <DialogClose asChild>
                                    <Button onClick={handleUpdateAccount} className="bg-white text-[#0A0F1C]">Save Changes</Button>
                                  </DialogClose>
                                </DialogFooter>
                              </DialogContent>
                            </Dialog>
                          </div>
                          <CardDescription>{account.account_type} Account</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Balance</span>
                            <span className={`font-mono font-semibold ${account.account_type === 'Spend' ? 'text-emerald-400' : 'text-red-400'}`}>
                              {formatCurrency(account.current_balance)}
                            </span>
                          </div>
                          <Separator />
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Account Number</span>
                            <span className="font-mono text-sm">{account.account_number_masked}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm text-muted-foreground">Sort Code</span>
                            <span className="font-mono text-sm">{account.sort_code_display}</span>
                          </div>
                          <Separator />
                          <div className="flex justify-between items-center">
                            <span className="text-sm text-muted-foreground">Published</span>
                            {account.published_status ? (
                              <Badge className="bg-success/20 text-emerald-400 border-success">Yes</Badge>
                            ) : (
                              <Badge variant="outline" className="text-yellow-400 border-yellow-400">No</Badge>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-heading text-2xl font-semibold">Transaction Management</h2>
              <Dialog>
                <DialogTrigger asChild>
                  <Button className="bg-white text-[#0A0F1C]" data-testid="add-transaction-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add Transaction
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-card border-border">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Add New Transaction</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>User</Label>
                      <Select value={newTransaction.user_id} onValueChange={(v) => setNewTransaction({...newTransaction, user_id: v, account_id: ''})}>
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select user" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {summary.map(item => (
                            <SelectItem key={item.user.id} value={item.user.id}>{item.user.internal_code}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Account</Label>
                      <Select value={newTransaction.account_id} onValueChange={(v) => setNewTransaction({...newTransaction, account_id: v})}>
                        <SelectTrigger className="bg-background">
                          <SelectValue placeholder="Select account" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          {getUserAccounts(newTransaction.user_id).map(acc => (
                            <SelectItem key={acc.id} value={acc.id}>{acc.display_name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Date</Label>
                      <Input 
                        type="date"
                        value={newTransaction.date} 
                        onChange={(e) => setNewTransaction({...newTransaction, date: e.target.value})}
                        className="bg-background"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input 
                        value={newTransaction.description} 
                        onChange={(e) => setNewTransaction({...newTransaction, description: e.target.value})}
                        className="bg-background"
                        placeholder="e.g., Amazon, Uber, Top up"
                      />
                    </div>
                    <div>
                      <Label>Type</Label>
                      <Select value={newTransaction.debit_or_credit} onValueChange={(v) => setNewTransaction({...newTransaction, debit_or_credit: v})}>
                        <SelectTrigger className="bg-background">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border">
                          <SelectItem value="Debit">Debit</SelectItem>
                          <SelectItem value="Credit">Credit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Amount</Label>
                      <Input 
                        type="number"
                        step="0.01"
                        value={newTransaction.amount} 
                        onChange={(e) => setNewTransaction({...newTransaction, amount: e.target.value})}
                        className="bg-background"
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild>
                      <Button variant="outline">Cancel</Button>
                    </DialogClose>
                    <DialogClose asChild>
                      <Button onClick={handleCreateTransaction} className="bg-white text-[#0A0F1C]">Add Transaction</Button>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <Tabs defaultValue={summary[0]?.user?.id} className="space-y-4">
              <TabsList className="bg-secondary">
                {summary.map(item => (
                  <TabsTrigger key={item.user.id} value={item.user.id} className="data-[state=active]:bg-accent">
                    {item.user.internal_code}
                  </TabsTrigger>
                ))}
              </TabsList>
              
              {summary.map(item => (
                <TabsContent key={item.user.id} value={item.user.id}>
                  <Card className="bg-card border-border">
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-border hover:bg-transparent">
                            <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Date</TableHead>
                            <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Account</TableHead>
                            <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Description</TableHead>
                            <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Type</TableHead>
                            <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">Amount</TableHead>
                            <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Status</TableHead>
                            <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {getUserTransactions(item.user.id).map((tx, idx) => {
                            const account = accounts.find(a => a.id === tx.account_id);
                            return (
                              <TableRow key={tx.id} className="border-border" data-testid={`tx-row-${idx}`}>
                                <TableCell className="text-sm">{formatDate(tx.date)}</TableCell>
                                <TableCell>
                                  <Badge variant="outline" className={account?.account_type === 'Spend' ? 'border-emerald-400/50 text-emerald-400' : 'border-red-400/50 text-red-400'}>
                                    {account?.account_type || 'Unknown'}
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-sm font-medium">{tx.description}</TableCell>
                                <TableCell>
                                  <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                                    tx.debit_or_credit === 'Credit' ? 'text-emerald-400' : 'text-red-400'
                                  }`}>
                                    {tx.debit_or_credit === 'Credit' 
                                      ? <ArrowDownRight className="w-3 h-3" /> 
                                      : <ArrowUpRight className="w-3 h-3" />}
                                    {tx.debit_or_credit}
                                  </span>
                                </TableCell>
                                <TableCell className="text-sm font-mono text-right">{formatCurrency(tx.amount)}</TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    {tx.published_status ? (
                                      <Badge className="bg-success/20 text-emerald-400 border-success text-xs">Published</Badge>
                                    ) : (
                                      <Badge variant="outline" className="text-yellow-400 border-yellow-400 text-xs">Draft</Badge>
                                    )}
                                    {tx.conflict_flag && (
                                      <Badge variant="outline" className="text-orange-400 border-orange-400 text-xs">Conflict</Badge>
                                    )}
                                    {tx.duplicate_flag && (
                                      <Badge variant="outline" className="text-red-400 border-red-400 text-xs">Duplicate</Badge>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-1">
                                    <Dialog>
                                      <DialogTrigger asChild>
                                        <Button variant="ghost" size="sm" onClick={() => setEditTransaction({...tx})} data-testid={`edit-tx-${tx.id}`}>
                                          <Pencil className="w-3 h-3" />
                                        </Button>
                                      </DialogTrigger>
                                      <DialogContent className="bg-card border-border">
                                        <DialogHeader>
                                          <DialogTitle className="font-heading">Edit Transaction</DialogTitle>
                                        </DialogHeader>
                                        <div className="space-y-4">
                                          <div>
                                            <Label>Date</Label>
                                            <Input 
                                              type="date"
                                              value={editTransaction?.date || ''} 
                                              onChange={(e) => setEditTransaction({...editTransaction, date: e.target.value})}
                                              className="bg-background"
                                            />
                                          </div>
                                          <div>
                                            <Label>Description</Label>
                                            <Input 
                                              value={editTransaction?.description || ''} 
                                              onChange={(e) => setEditTransaction({...editTransaction, description: e.target.value})}
                                              className="bg-background"
                                            />
                                          </div>
                                          <div>
                                            <Label>Type</Label>
                                            <Select value={editTransaction?.debit_or_credit} onValueChange={(v) => setEditTransaction({...editTransaction, debit_or_credit: v})}>
                                              <SelectTrigger className="bg-background">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent className="bg-card border-border">
                                                <SelectItem value="Debit">Debit</SelectItem>
                                                <SelectItem value="Credit">Credit</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                          <div>
                                            <Label>Amount</Label>
                                            <Input 
                                              type="number"
                                              step="0.01"
                                              value={editTransaction?.amount || ''} 
                                              onChange={(e) => setEditTransaction({...editTransaction, amount: parseFloat(e.target.value)})}
                                              className="bg-background"
                                            />
                                          </div>
                                        </div>
                                        <DialogFooter>
                                          <DialogClose asChild>
                                            <Button variant="outline">Cancel</Button>
                                          </DialogClose>
                                          <DialogClose asChild>
                                            <Button onClick={handleUpdateTransaction} className="bg-white text-[#0A0F1C]">Save Changes</Button>
                                          </DialogClose>
                                        </DialogFooter>
                                      </DialogContent>
                                    </Dialog>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      onClick={() => handleDeleteTransaction(tx.id)}
                                      className="text-destructive hover:text-destructive"
                                      data-testid={`delete-tx-${tx.id}`}
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {getUserTransactions(item.user.id).length === 0 && (
                            <TableRow>
                              <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                                No transactions
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                </TabsContent>
              ))}
            </Tabs>
          </div>
        )}

        {/* Upload Tab */}
        {activeTab === 'upload' && (
          <div className="animate-fade-in">
            <h2 className="font-heading text-2xl font-semibold mb-6">Upload Transactions</h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Upload CSV / Excel</CardTitle>
                  <CardDescription>Import transactions from a file</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>User</Label>
                    <Select value={uploadUserId} onValueChange={(v) => { setUploadUserId(v); setUploadAccountId(''); }}>
                      <SelectTrigger className="bg-background" data-testid="upload-user-select">
                        <SelectValue placeholder="Select user" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {summary.map(item => (
                          <SelectItem key={item.user.id} value={item.user.id}>{item.user.internal_code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Account</Label>
                    <Select value={uploadAccountId} onValueChange={setUploadAccountId}>
                      <SelectTrigger className="bg-background" data-testid="upload-account-select">
                        <SelectValue placeholder="Select account" />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-border">
                        {getUserAccounts(uploadUserId).map(acc => (
                          <SelectItem key={acc.id} value={acc.id}>{acc.display_name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>File</Label>
                    <Input 
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      onChange={(e) => setUploadFile(e.target.files[0])}
                      className="bg-background cursor-pointer"
                      data-testid="upload-file-input"
                    />
                  </div>
                  <Button 
                    onClick={handleUpload}
                    className="w-full bg-white text-[#0A0F1C]"
                    disabled={!uploadFile || !uploadUserId || !uploadAccountId}
                    data-testid="upload-submit-btn"
                  >
                    <Upload className="w-4 h-4 mr-2" /> Upload File
                  </Button>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle className="font-heading text-lg">Import Template</CardTitle>
                  <CardDescription>Use this format for uploads</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="bg-background rounded-lg p-4 font-mono text-sm">
                    <p className="text-muted-foreground mb-2">Required columns:</p>
                    <code className="block text-emerald-400">date,description,debit_or_credit,amount</code>
                    <Separator className="my-4" />
                    <p className="text-muted-foreground mb-2">Example:</p>
                    <code className="block text-xs">
                      2026-04-01,Amazon,Debit,42.50<br/>
                      2026-04-02,Top up,Credit,300.00<br/>
                      2026-04-03,Uber,Debit,18.20
                    </code>
                  </div>
                  <Button variant="outline" className="w-full mt-4" data-testid="download-template-btn">
                    <Download className="w-4 h-4 mr-2" /> Download Template
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Upload History */}
            <Card className="bg-card border-border mt-6">
              <CardHeader>
                <CardTitle className="font-heading text-lg">Upload History</CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Date</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">User</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">File</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Added</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Duplicates</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {uploads.map((upload, idx) => {
                      const uploadUser = summary.find(s => s.user.id === upload.user_id);
                      return (
                        <TableRow key={upload.id} className="border-border" data-testid={`upload-row-${idx}`}>
                          <TableCell className="text-sm">{formatDate(upload.upload_date)}</TableCell>
                          <TableCell>
                            <Badge variant="outline">{uploadUser?.user?.internal_code || 'Unknown'}</Badge>
                          </TableCell>
                          <TableCell className="text-sm font-mono">{upload.file_name}</TableCell>
                          <TableCell className="text-sm text-emerald-400">{upload.transactions_added}</TableCell>
                          <TableCell className="text-sm text-yellow-400">{upload.duplicates_found}</TableCell>
                        </TableRow>
                      );
                    })}
                    {uploads.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No uploads yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Publish Tab */}
        {activeTab === 'publish' && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="font-heading text-2xl font-semibold">Publish Controls</h2>
                <p className="text-muted-foreground mt-1">Manage what data is visible on user dashboards. Only published items are shown to users.</p>
              </div>
              <div className="flex items-center gap-2">
                <Select value={publishUserFilter} onValueChange={setPublishUserFilter}>
                  <SelectTrigger className="w-40 bg-background" data-testid="publish-user-filter">
                    <SelectValue placeholder="Filter by user" />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="all">All Users</SelectItem>
                    {summary.map(item => (
                      <SelectItem key={item.user.id} value={item.user.id}>{item.user.internal_code}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Quick Actions per User */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
              {summary.map(item => {
                const userAccounts = getUserAccounts(item.user.id);
                const userTransactions = getUserTransactions(item.user.id);
                const publishedAccounts = userAccounts.filter(a => a.published_status).length;
                const publishedTx = userTransactions.filter(t => t.published_status).length;
                const draftTx = userTransactions.filter(t => !t.published_status).length;
                
                return (
                  <Card key={item.user.id} className="bg-card border-border" data-testid={`publish-summary-${item.user.internal_code}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <Badge variant="outline" className="bg-accent/20 border-accent text-white">
                          {item.user.internal_code}
                        </Badge>
                        <div className="flex gap-1">
                          <Button 
                            size="sm"
                            className="bg-success hover:bg-success/90 h-7 text-xs"
                            onClick={() => handlePublish(item.user.id)}
                            data-testid={`quick-publish-${item.user.internal_code}`}
                          >
                            Publish All
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            onClick={() => handleUnpublish(item.user.id)}
                            data-testid={`quick-unpublish-${item.user.internal_code}`}
                          >
                            Unpublish All
                          </Button>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div>
                          <p className="text-muted-foreground">Accounts</p>
                          <p className="font-medium">{publishedAccounts}/3</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Published</p>
                          <p className="font-medium text-emerald-400">{publishedTx}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Drafts</p>
                          <p className="font-medium text-yellow-400">{draftTx}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* Accounts Section */}
            <Card className="bg-card border-border mb-6">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <CreditCard className="w-5 h-5" />
                      Account Publishing
                    </CardTitle>
                    <CardDescription>Toggle visibility of account balances and details</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedAccounts.length > 0 && (
                      <>
                        <span className="text-sm text-muted-foreground">{selectedAccounts.length} selected</span>
                        <Button 
                          size="sm" 
                          className="bg-success hover:bg-success/90"
                          onClick={() => handlePublishSelectedAccounts(true)}
                          data-testid="publish-selected-accounts"
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Publish Selected
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handlePublishSelectedAccounts(false)}
                          data-testid="unpublish-selected-accounts"
                        >
                          <XCircle className="w-3 h-3 mr-1" /> Unpublish Selected
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={accounts.filter(a => publishUserFilter === 'all' || a.user_id === publishUserFilter).every(a => selectedAccounts.includes(a.id)) && accounts.length > 0}
                          onCheckedChange={() => handleSelectAllAccounts(publishUserFilter === 'all' ? null : publishUserFilter)}
                          data-testid="select-all-accounts"
                        />
                      </TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">User</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Account</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">Balance</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground text-center">Status</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground text-center">Publish</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {accounts
                      .filter(a => publishUserFilter === 'all' || a.user_id === publishUserFilter)
                      .map((account, idx) => {
                        const accountUser = summary.find(s => s.user.id === account.user_id);
                        const isTreasury = account.account_type === 'Treasury';
                        return (
                          <TableRow key={account.id} className={`border-border ${isTreasury ? 'bg-secondary/20' : ''}`} data-testid={`account-publish-row-${idx}`}>
                            <TableCell>
                              <Checkbox 
                                checked={selectedAccounts.includes(account.id)}
                                onCheckedChange={() => handleSelectAccount(account.id)}
                                data-testid={`select-account-${account.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{accountUser?.user?.internal_code || 'Unknown'}</Badge>
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{account.display_name}</p>
                                <p className={`text-xs ${isTreasury ? 'text-blue-400' : 'text-muted-foreground'}`}>
                                  {isTreasury ? 'Informational - Balance Only' : `${account.account_type} Account`}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell className={`text-right font-mono ${
                              account.account_type === 'Spend' ? 'text-emerald-400' : 
                              account.account_type === 'Hold' ? 'text-red-400' : 'text-blue-400'
                            }`}>
                              {formatCurrency(account.current_balance)}
                            </TableCell>
                            <TableCell className="text-center">
                              {account.published_status ? (
                                <Badge className="bg-success/20 text-emerald-400 border-success text-xs">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Published
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-yellow-400 border-yellow-400 text-xs">
                                  <Circle className="w-3 h-3 mr-1" /> Draft
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch 
                                checked={account.published_status}
                                onCheckedChange={() => handleToggleAccountPublish(account.id)}
                                data-testid={`toggle-account-${account.id}`}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            {/* Transactions Section */}
            <Card className="bg-card border-border">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg flex items-center gap-2">
                      <Receipt className="w-5 h-5" />
                      Transaction Publishing
                    </CardTitle>
                    <CardDescription>Control which transactions appear on user dashboards</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedTransactions.length > 0 && (
                      <>
                        <span className="text-sm text-muted-foreground">{selectedTransactions.length} selected</span>
                        <Button 
                          size="sm" 
                          className="bg-success hover:bg-success/90"
                          onClick={() => handlePublishSelectedTransactions(true)}
                          data-testid="publish-selected-transactions"
                        >
                          <CheckCircle2 className="w-3 h-3 mr-1" /> Publish Selected
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => handlePublishSelectedTransactions(false)}
                          data-testid="unpublish-selected-transactions"
                        >
                          <XCircle className="w-3 h-3 mr-1" /> Unpublish Selected
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="w-12">
                        <Checkbox 
                          checked={transactions.filter(t => publishUserFilter === 'all' || t.user_id === publishUserFilter).every(t => selectedTransactions.includes(t.id)) && transactions.length > 0}
                          onCheckedChange={() => handleSelectAllTransactions(publishUserFilter === 'all' ? null : publishUserFilter)}
                          data-testid="select-all-transactions"
                        />
                      </TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">User</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Date</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Description</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Type</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground text-right">Amount</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground text-center">Status</TableHead>
                      <TableHead className="text-xs font-bold uppercase tracking-wider text-muted-foreground text-center">Publish</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {transactions
                      .filter(t => publishUserFilter === 'all' || t.user_id === publishUserFilter)
                      .map((tx, idx) => {
                        const txUser = summary.find(s => s.user.id === tx.user_id);
                        const txAccount = accounts.find(a => a.id === tx.account_id);
                        return (
                          <TableRow key={tx.id} className="border-border" data-testid={`tx-publish-row-${idx}`}>
                            <TableCell>
                              <Checkbox 
                                checked={selectedTransactions.includes(tx.id)}
                                onCheckedChange={() => handleSelectTransaction(tx.id)}
                                data-testid={`select-tx-${tx.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <Badge variant="outline" className="text-xs w-fit">{txUser?.user?.internal_code || 'Unknown'}</Badge>
                                <span className={`text-xs ${txAccount?.account_type === 'Spend' ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {txAccount?.account_type || 'Unknown'}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{formatDate(tx.date)}</TableCell>
                            <TableCell className="text-sm font-medium">{tx.description}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                                tx.debit_or_credit === 'Credit' ? 'text-emerald-400' : 'text-red-400'
                              }`}>
                                {tx.debit_or_credit === 'Credit' 
                                  ? <ArrowDownRight className="w-3 h-3" /> 
                                  : <ArrowUpRight className="w-3 h-3" />}
                                {tx.debit_or_credit}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm font-mono text-right">{formatCurrency(tx.amount)}</TableCell>
                            <TableCell className="text-center">
                              {tx.published_status ? (
                                <Badge className="bg-success/20 text-emerald-400 border-success text-xs">
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Published
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-yellow-400 border-yellow-400 text-xs">
                                  <Circle className="w-3 h-3 mr-1" /> Draft
                                </Badge>
                              )}
                              {tx.conflict_flag && (
                                <Badge variant="outline" className="text-orange-400 border-orange-400 text-xs ml-1">
                                  <AlertTriangle className="w-3 h-3" />
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-center">
                              <Switch 
                                checked={tx.published_status}
                                onCheckedChange={() => handleToggleTransactionPublish(tx.id)}
                                data-testid={`toggle-tx-${tx.id}`}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    {transactions.filter(t => publishUserFilter === 'all' || t.user_id === publishUserFilter).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                          No transactions found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Mercury Transaction Operations Tab */}
        {activeTab === 'mercury' && (
          <div className="animate-fade-in">
            <h2 className="font-heading text-2xl font-semibold mb-6">Transaction Operations</h2>
            <TransactionOperations />
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
